import {AppName, Env} from '@hconnect/common/hproduce/types'
import {
  generateNotificationChannels,
  Notifications,
  NotificationsOptions
} from '@hconnect/common/notifications'
import {useUserContext} from '@hconnect/login'
import {Shell as ShellWrapper, CommonHeader, NavItem} from '@hconnect/uikit/src/lib2'
import {CommonHeaderGuidedTourProps} from '@hconnect/uikit/src/lib2/shell/header/CommonHeader'
import {getPlantName, useUpmPlantsContext} from '@hconnect/upm'
import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {useNavigate, useLocation, useParams} from 'react-router'

import {NOTIFICATIONS_TRANSLATION_KEYS} from '../consts'
import {useSupportedLanguages} from '../hooks/useSupportedLanguages'
import {getSystemTimezone} from '../hooks/useTimezone'
import {Context} from '../types/context.types'

import {Footer} from './Footer'

type Props = {
  context: Context
  appName?: AppName
  enableNotifications?: boolean
  children: React.ReactNode
  guidedTours?: CommonHeaderGuidedTourProps
  navItems?: NavItem[]
}

type PathParameter = {
  plantId?: string
}

export const Shell: React.FC<Props> = ({
  appName = 'HProduce',
  enableNotifications,
  context,
  children,
  guidedTours,
  navItems
}) => {
  const {
    i18n: {language}
  } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const supportedLanguages = useSupportedLanguages()
  const {loggedUser} = useUserContext()
  const userProfile = {
    email: loggedUser?.eMail ?? '',
    name: loggedUser?.name ?? ''
  }

  const {plantId} = useParams<PathParameter>()

  const upmPlants = useUpmPlantsContext()

  const timeZone = getSystemTimezone()

  const options = useMemo<NotificationsOptions | undefined>(() => {
    if (!enableNotifications) return undefined

    const channels = generateNotificationChannels(context.whiteList)
    return channels ? {channels, countryCode: language.toUpperCase().split('-')[0]} : undefined
  }, [language, context.whiteList, enableNotifications])

  return (
    <ShellWrapper
      isResponsive
      boxed={false}
      header={
        <CommonHeader
          appName={appName}
          onLogout={context.onLogout}
          additionalActions={
            options
              ? [
                  <Notifications
                    key="notification-provider"
                    options={options}
                    translations={NOTIFICATIONS_TRANSLATION_KEYS}
                    timezone={timeZone}
                  />
                ]
              : undefined
          }
          supportedLanguages={supportedLanguages}
          user={userProfile}
          navItems={navItems}
          env={process.env.REACT_APP_STAGE as Env}
          whiteList={context.whiteList}
          navigate={(url) => navigate(url)}
          locationPath={location.pathname}
          enableMyTasks
          plant={plantId ? {plantId, plantName: getPlantName(upmPlants, plantId)} : undefined}
          upmPlants={upmPlants}
          guidedTours={guidedTours}
          closeDropdownOnLanguageSwitch
        />
      }
      footer={<Footer />}
    >
      {children}
    </ShellWrapper>
  )
}
